import { supabase } from '../config/supabase';
import type { Job, JobLifecycleEvent } from '../types/database';
import type { FleetOperatorRecommendation } from './fleetOperatorDispatch';

export async function requestJobPlatformAssist(
  jobId: string,
  reason?: string
): Promise<Job> {
  const { data, error } = await supabase.rpc('request_job_platform_assist', {
    p_job_id: jobId,
    p_reason: reason ?? null,
  });
  if (error) throw new Error(error.message);
  return data as Job;
}

export async function cancelJobPlatformAssist(jobId: string): Promise<Job> {
  const { data, error } = await supabase.rpc('cancel_job_platform_assist', {
    p_job_id: jobId,
  });
  if (error) throw new Error(error.message);
  return data as Job;
}

export async function reassignJobFleetFromAssist(params: {
  jobId: string;
  newFleetOperatorId: string;
  note?: string;
}): Promise<Job> {
  const { data, error } = await supabase.rpc('reassign_job_fleet_from_assist', {
    p_job_id: params.jobId,
    p_new_fleet_operator_id: params.newFleetOperatorId,
    p_note: params.note ?? null,
  });
  if (error) throw new Error(error.message);
  return data as Job;
}

export async function recommendFleetOperatorsForJob(
  jobId: string,
  limit = 5,
  requireAcceptingTasks = false
): Promise<FleetOperatorRecommendation[]> {
  const { data, error } = await supabase.rpc('recommend_fleet_operators_for_job', {
    p_job_id: jobId,
    p_limit: limit,
    p_require_accepting_tasks: requireAcceptingTasks,
  });
  if (error) throw new Error(error.message);
  return (data ?? []) as FleetOperatorRecommendation[];
}

export async function listJobLifecycleEvents(
  jobId: string
): Promise<JobLifecycleEvent[]> {
  const { data, error } = await supabase.rpc('list_job_lifecycle_events', {
    p_job_id: jobId,
  });
  if (error) throw new Error(error.message);
  return (data ?? []) as JobLifecycleEvent[];
}

/** Fleet may write only when owning and not pending platform assist. */
export function isJobWritableForFleet(
  job: Pick<Job, 'fleet_operator_id' | 'assist_status'>,
  fleetOperatorId: string | null | undefined
): boolean {
  if (!fleetOperatorId) return false;
  if (job.fleet_operator_id !== fleetOperatorId) return false;
  if (job.assist_status === 'pending_platform') return false;
  return true;
}
